import { z } from 'zod';
/**
 * Contrats du JOURNAL D'AUDIT (`audit_log`) — consultation superadmin (audit B1).
 *
 * `audit_log` est écrite depuis 14 endroits (connexion, changement de mot de passe,
 * réinitialisation, et TOUS les jobs de traitement) et n'avait JUSQU'ICI aucun lecteur
 * en production : personne ne pouvait répondre à « qui a supprimé / déplacé / publié
 * quoi ». Ce module pose le contrat de la page de consultation, calqué sur celui des
 * journaux de requêtes (`admin.ts`, `requestLog*`) qui est le modèle du projet.
 *
 * ⚠ `details` est un `jsonb` NON BORNÉ, rempli LIBREMENT par chaque site d'écriture
 * (compteurs de job, params, etc.). Il n'y a AUCUN schéma commun et il ne faut PAS en
 * présumer un : le contrat le type en `unknown` et l'affichage doit rester robuste à
 * n'importe quelle forme (y compris un scalaire ou un tableau).
 *
 * ⚠ PIÈGE ZOD v4 (boot crash) : `auditLogQuerySchema` est la QUERY d'une route, donc
 * passée par `z.toJSONSchema(…, { target: 'draft-7' })` au démarrage. Aucun
 * `.transform()` ici (uniquement `.coerce`/`.optional`/`.default`/`.regex`, tous
 * représentables). Garde anti-régression : `route-json-schema.test.ts`.
 */
/** Borne haute de la fenêtre de listing (protège la DB + le rendu). */
export const AUDIT_LOG_MAX_LIMIT = 200;
export const AUDIT_LOG_DEFAULT_LIMIT = 50;
/**
 * Une entrée du journal d'audit (`GET /api/admin/audit-log`).
 *
 * `userId`/`userEmail` = AUTEUR de l'action. `userEmail` provient d'une jointure sur
 * `users` (l'e-mail n'est pas dupliqué dans `audit_log`, contrairement à
 * `request_logs`) et vaut `null` quand `user_id` est nul — c'est-à-dire pour toute
 * action non imputée. Aucun secret ne transite ici.
 */
export const auditLogEntrySchema = z.object({
    id: z.uuid(),
    createdAt: z.string().datetime({ offset: true }),
    /** Verbe métier libre, p.ex. `auth.login`, `asset.move`, `trash.purge`. */
    action: z.string(),
    /** Nature de la cible (`library`, `user`…). */
    targetType: z.string(),
    /** Identifiant de la cible (texte : pas toujours un uuid), ou `null`. */
    targetId: z.string().nullable(),
    /** L'action était une SIMULATION (aperçu) : rien n'a été modifié. */
    dryRun: z.boolean(),
    /** Résumé lisible rédigé par le site d'écriture, ou `null`. */
    summary: z.string().nullable(),
    /**
     * Charge utile `jsonb` LIBRE. Forme HÉTÉROGÈNE et non garantie — ne jamais
     * présumer d'un schéma ni indexer un champ précis sans garde.
     */
    details: z.unknown().nullable(),
    userId: z.uuid().nullable(),
    /** E-mail de l'auteur (jointure `users`), ou `null` si l'action n'est pas imputée. */
    userEmail: z.string().nullable(),
});
/** Page du journal (`{ entries, total }`) — `total` dimensionne la pagination. */
export const auditLogListSchema = z.object({
    entries: z.array(auditLogEntrySchema),
    total: z.number().int().nonnegative(),
});
/**
 * Verbes d'action RÉELLEMENT présents dans le journal (`GET
 * /api/admin/audit-log/actions`) — alimente la liste déroulante du filtre « Action ».
 *
 * Volontairement DÉRIVÉ des données et non d'un enum figé : `audit_log.action` est une
 * colonne `text` libre, chaque site d'écriture choisit son verbe, et un enum partagé
 * deviendrait faux au premier job ajouté. La liste est donc toujours exacte.
 */
export const auditLogActionsSchema = z.object({
    actions: z.array(z.string()),
});
/**
 * Query params de `GET /api/admin/audit-log`. `limit` borné (1..200, défaut 50),
 * `offset` ≥ 0. Coercition depuis la querystring via `z.coerce.number()` (AJV coerce
 * la string, Zod re-valide) — même technique que `requestLogQuerySchema`.
 *
 * Trois filtres optionnels, composables (ET) :
 *  - `action` : verbe exact (jamais un `LIKE` : la liste vient de la base) ;
 *  - `userId` : auteur de l'action ;
 *  - `from`/`to` : bornes de date INCLUSIVE/EXCLUSIVE sur `created_at`.
 *
 * `action` est borné en longueur ET restreint au jeu de caractères réellement utilisé
 * par les verbes (`a-z`, `_`, `.`) — pas pour la sécurité (le SQL est paramétré, un
 * `=` ne peut rien injecter) mais pour refuser tôt une valeur qui ne peut rien matcher.
 */
export const auditLogQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(AUDIT_LOG_MAX_LIMIT)
        .default(AUDIT_LOG_DEFAULT_LIMIT),
    offset: z.coerce.number().int().nonnegative().default(0),
    action: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9_.]+$/)
        .optional(),
    userId: z.uuid().optional(),
    /** Borne basse INCLUSIVE (`created_at >= from`), ISO 8601 avec offset. */
    from: z.string().datetime({ offset: true }).optional(),
    /** Borne haute EXCLUSIVE (`created_at < to`), ISO 8601 avec offset. */
    to: z.string().datetime({ offset: true }).optional(),
});
//# sourceMappingURL=audit-log.js.map